import React, { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Modal,
  ModalBody,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import { getLotById, deleteLot } from "../../api/lot.api";
import { toast } from "react-toastify";
import { titleCase } from "../../utils/case";
import { formatLeadTime } from "../../utils/leadTimeFormatter";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { hasRole } from "../../utils/auth";
import { formatTime } from "../../utils/datetime";

const LotView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManageLots = hasRole(
    "superadmin",
    "finance"
  );
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [lot, setLot] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchLot = async () => {
      try {
        const data = await getLotById(id);
        setLot(data);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLot();
  }, [id]);

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!lot) {
    return <div className="text-center mt-5">Lot not found</div>;
  }

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const parsedPricing = lot.pricing
    ? typeof lot.pricing === "string"
      ? JSON.parse(lot.pricing)
      : lot.pricing
    : [];

  const groupedPricing = parsedPricing.reduce((acc, item) => {
    if (!acc[item.chargedBy]) {
      acc[item.chargedBy] = {
        chargedBy: item.chargedBy,
        nonPeakAmount: null,
        peakAmount: null,
      };
    }

    if (item.season === "non_peak") {
      acc[item.chargedBy].nonPeakAmount = item.amount;
    }

    if (item.season === "peak") {
      acc[item.chargedBy].peakAmount = item.amount;
    }

    return acc;
  }, {});

  const pricingRows = groupedPricing ? Object.values(groupedPricing) : [];
  const lotAddOns = lot.lotAddOns || [];
  const isExpired = (() => {
    if (!lot.expiredAt) return false;

    const today = new Date();
    const todayDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    return lot.expiredAt < todayDate;
  })();

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("lot"))}
        title={titleCase(t("view lot"))}
        mainTitle={
          <div className="d-flex align-items-center gap-2">
            <span>{lot.name} @ {lot.location}</span>
            <Badge
              className="m-l-5"
              color={isExpired ? "danger" : lot.status === "active" ? "success" : "dark"}
            >
              {isExpired ? titleCase(t("expired")) : titleCase(t(lot.status))}
            </Badge>
          </div>
        }
      />

      <Container fluid>
        <Row className="mb-4">
          <Col className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-sm btn-light"
              onClick={() => scrollToSection("add-ons")}
            >
              {titleCase(t("add ons"))}
            </button>

            <button
              className="btn btn-sm btn-light"
              onClick={() => scrollToSection("remarks")}
            >
              {titleCase(t("remarks"))}
            </button>

            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#ddd",
                margin: "0 8px",
              }}
            />

            {canManageLots && (
              <>
                <button
                  className="btn btn-sm btn-primary"
                  disabled={isExpired}
                  title={isExpired ? t("expired lots cannot be edited") : undefined}
                  onClick={() => navigate(`/lots/edit/${id}`)}
                >
                  {titleCase(t("edit"))}
                </button>

                <button
                  className="btn btn-sm btn-danger"
                  disabled={deleting}
                  onClick={async () => {
                    const confirmed = window.confirm(
                      t("are you sure you want to delete this lot?")
                    );

                    if (!confirmed) return;

                    try {
                      setDeleting(true);
                      await deleteLot(id);
                      toast.success(t("deleted successfully"));
                      navigate("/lots");
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setDeleting(false);
                    }
                  }}
                >
                  {deleting ? t("deleting...") : titleCase(t("delete"))}
                </button>
              </>
            )}

            <button
              className="btn btn-sm btn-dark"
              onClick={() => navigate(-1)}
            >
              {titleCase(t("back"))}
            </button>
          </Col>
        </Row>

        <Row>
          <Col>
            {lot.photos?.length ? (
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={16}
                slidesPerView={5}
                breakpoints={{
                  0: { slidesPerView: 1 },
                  576: { slidesPerView: 2 },
                  768: { slidesPerView: 3 },
                  992: { slidesPerView: 4 },
                  1200: { slidesPerView: 5 },
                }}
              >
                {lot.photos.map((photo) => (
                  <SwiperSlide key={photo}>
                    <img
                      src={`${process.env.REACT_APP_FILES_URL}/${photo}`}
                      alt=""
                      onClick={() => setPreviewPhoto(photo)}
                      style={{
                        width: "100%",
                        height: 180,
                        objectFit: "cover",
                        borderRadius: 10,
                        cursor: "zoom-in",
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <p className="text-muted">No images</p>
            )}
          </Col>
        </Row>

        <Row className="mb-4 align-items-stretch">
          <Col md="4" className="d-flex">
            <Card className="mb-4 shadow-sm w-100 h-100">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("description"))}
                </H5>
              </CardHeader>
              <CardBody>
                <p className="text-muted mb-0" style={{ whiteSpace: "pre-line" }}>
                  {lot.description || "-"}
                </p>
              </CardBody>
            </Card>
          </Col>

          <Col md="8">
            <Row className="align-items-stretch">
              <Col md="6" className="d-flex">
                <Card className="mb-4 shadow-sm w-100 h-100">
                  <CardHeader>
                    <H5 className="mb-0">
                      {titleCase(t("configuration"))}
                    </H5>
                  </CardHeader>
                  <CardBody>
                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="6">
                          <p className="text-muted mb-0">
                            {titleCase(t("capacity"))}
                          </p>
                        </Col>
                        <Col md="6">
                          <h6 className="mb-0">{lot.capacity ?? "-"}</h6>
                        </Col>
                      </Row>
                    </div>

                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="6">
                          <p className="text-muted mb-0">
                            {titleCase(t("allow additional guests"))}
                          </p>
                        </Col>
                        <Col md="6">
                          <Badge
                            color={lot.allowAdditionalGuests ? "success" : "danger"}
                          >
                            {lot.allowAdditionalGuests ? t("on") : t("off")}
                          </Badge>
                        </Col>
                      </Row>
                    </div>

                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="6">
                          <p className="text-muted mb-0">
                            {titleCase(t("add on required"))}
                          </p>
                        </Col>
                        <Col md="6">
                          <Badge color={lot.addOnRequired ? "success" : "danger"}>
                            {lot.addOnRequired ? t("on") : t("off")}
                          </Badge>
                        </Col>
                      </Row>
                    </div>

                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="6">
                          <p className="text-muted mb-0">
                            {titleCase(t("expired at"))}
                          </p>
                        </Col>
                        <Col md="6">
                          <h6 className="mb-0">{lot.expiredAt || "-"}</h6>
                        </Col>
                      </Row>
                    </div>

                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="6">
                          <p className="text-muted mb-0">
                            {titleCase(t("type"))}
                          </p>
                        </Col>
                        <Col md="6">
                          <h6 className="mb-0">
                            {titleCase(t(lot.type || "permanent"))}
                          </h6>
                        </Col>
                      </Row>
                    </div>

                    <div className="p-3 border rounded">
                      <Row className="align-items-center">
                        <Col md="6">
                          <p className="text-muted mb-0">
                            {titleCase(t("configuration type"))}
                          </p>
                        </Col>
                        <Col md="6">
                          <h6 className="mb-0">
                            {lot.isEvent
                              ? titleCase(t("event"))
                              : lot.isDayTrip
                              ? titleCase(t("day trip"))
                              : titleCase(t("standard"))}
                          </h6>
                        </Col>
                      </Row>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col md="6" className="d-flex">
                <Card className="mb-4 shadow-sm w-100 h-100" id="details">
                  <CardHeader>
                    <H5 className="mb-0">
                      {titleCase(t("booking settings"))}
                    </H5>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="12" className="mb-3">
                        <div className="p-3 border rounded">
                          <Row className="align-items-center">
                            <Col md="6">
                              <p className="text-muted mb-0">
                                {titleCase(t("allow public booking"))}
                              </p>
                            </Col>
                            <Col md="6">
                              <Badge
                                color={lot.allowPublicBooking ? "success" : "danger"}
                              >
                                {lot.allowPublicBooking ? t("on") : t("off")}
                              </Badge>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col md="12" className="mb-3">
                        <div className="p-3 border rounded">
                          <Row className="align-items-center">
                            <Col md="6">
                              <p className="text-muted mb-0">
                                {titleCase(t("requires approval"))}
                              </p>
                            </Col>
                            <Col md="6">
                              <Badge
                                color={lot.requiresApproval ? "success" : "danger"}
                              >
                                {lot.requiresApproval ? t("on") : t("off")}
                              </Badge>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col md="12" className="mb-3">
                        <div className="p-3 border rounded">
                          <Row className="align-items-center">
                            <Col md="6">
                              <p className="text-muted mb-0">
                                {titleCase(t("min lead time"))}
                              </p>
                            </Col>
                            <Col md="6">
                              <h6 className="mb-0">
                                {formatLeadTime(lot.minLeadTime, t)}
                              </h6>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col md="12" className="mb-3">
                        <div className="p-3 border rounded">
                          <Row className="align-items-center">
                            <Col md="6">
                              <p className="text-muted mb-0">
                                {titleCase(t("max lead time"))}
                              </p>
                            </Col>
                            <Col md="6">
                              <h6 className="mb-0">
                                {formatLeadTime(lot.maxLeadTime, t)}
                              </h6>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col md="12" className="mb-3">
                        <div className="p-3 border rounded">
                          <Row className="align-items-center">
                            <Col md="6">
                              <p className="text-muted mb-0">
                                {titleCase(t("check in time"))}
                              </p>
                            </Col>
                            <Col md="6">
                              <h6 className="mb-0">
                                {formatTime(lot.checkInTime
                                  ? lot.checkInTime.substring(0, 5)
                                  : "-")}
                              </h6>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col md="12">
                        <div className="p-3 border rounded">
                          <Row className="align-items-center">
                            <Col md="6">
                              <p className="text-muted mb-0">
                                {titleCase(t("check out time"))}
                              </p>
                            </Col>
                            <Col md="6">
                              <h6 className="mb-0">
                                {formatTime(lot.checkOutTime
                                  ? lot.checkOutTime.substring(0, 5)
                                  : "-")}
                              </h6>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md="12">
                <Card className="mb-4 shadow-sm">
                  <CardHeader>
                    <H5 className="mb-0">
                      {titleCase(t("payment details"))}
                    </H5>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="12" className="mb-3">
                        <div className="p-3 border rounded">
                          <Row className="align-items-center">
                            <Col md="6">
                              <p className="text-muted mb-0">
                                {titleCase(t("requires payment"))}
                              </p>
                            </Col>
                            <Col md="6">
                              <Badge
                                color={lot.requiresPayment ? "success" : "danger"}
                              >
                                {lot.requiresPayment ? t("on") : t("off")}
                              </Badge>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      {lot.requiresPayment && pricingRows.length > 0 ? (
                        <Col md="12">
                          <div className="table-responsive">
                            <table className="table table-bordered mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>{titleCase(t("charged by"))}</th>
                                  <th>{titleCase(t("standard price"))}</th>
                                  <th>{titleCase(t("peak season price"))}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pricingRows.map((pricing) => (
                                  <tr key={pricing.chargedBy}>
                                    <td>
                                      {titleCase(
                                        t(pricing.chargedBy.replace(/_/g, " "))
                                      )}
                                    </td>
                                    <td>
                                      {pricing.nonPeakAmount != null
                                        ? pricing.nonPeakAmount.toLocaleString("en-MY", {
                                            style: "currency",
                                            currency: "MYR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                        : "-"}
                                    </td>
                                    <td>
                                      {pricing.peakAmount != null
                                        ? pricing.peakAmount.toLocaleString("en-MY", {
                                            style: "currency",
                                            currency: "MYR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                        : "-"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Col>
                      ) : lot.requiresPayment ? (
                        <Col md="12">
                          <p className="mb-0 text-muted">-</p>
                        </Col>
                      ) : null}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row>
          <Col className="col-12">
            <Card className="mb-4 shadow-sm" id="add-ons">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("add ons"))}
                </H5>
              </CardHeader>

              <CardBody>
                {lotAddOns.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th width="90">{titleCase(t("photo"))}</th>
                          <th>{titleCase(t("name"))}</th>
                          <th>{titleCase(t("category"))}</th>
                          <th>{titleCase(t("inventory type"))}</th>
                          <th>{titleCase(t("minimum quantity"))}</th>
                          <th>{titleCase(t("maximum quantity"))}</th>
                          <th>{titleCase(t("status"))}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {lotAddOns.map((item) => (
                          <tr key={item.id}>
                            <td>
                              {item.addOn?.photo ? (
                                <img
                                  src={`${process.env.REACT_APP_FILES_URL}/${item.addOn.photo}`}
                                  alt={item.addOn.name}
                                  style={{
                                    width: 70,
                                    height: 70,
                                    objectFit: "contain",
                                    borderRadius: 8,
                                    border: "1px solid #ddd",
                                  }}
                                />
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {item.addOn?.name || "-"}
                              {" "}
                              {item.isEventPass && (
                                <Badge color="info">
                                  {titleCase(t("event pass"))}
                                </Badge>
                              )}
                            </td>
                            <td>{item.addOn?.category || "-"}</td>
                            <td>
                              {titleCase(
                                t(
                                  item.addOn?.inventoryType?.replace(/_/g, " ") ||
                                    "-"
                                )
                              )}
                            </td>
                            <td>{item.minQty ?? "-"}</td>
                            <td>{item.maxQty ?? "-"}</td>
                            <td>
                              <Badge
                                color={item.status === "active" ? "success" : "dark"}
                              >
                                {titleCase(t(item.status || "-"))}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mb-0 text-muted">-</p>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4 shadow-sm" id="remarks">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("remarks"))}
                </H5>
              </CardHeader>

              <CardBody>
                {lot.remark ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: lot.remark }}
                  />
                ) : (
                  <p className="mb-0 text-muted">-</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showScrollTop && (
          <button
            className="btn-primary"
            onClick={scrollToTop}
            style={{
              position: "fixed",
              bottom: "30px",
              right: "30px",
              zIndex: 999,
              borderRadius: "50%",
              width: "45px",
              height: "45px",
              border: "none",
              color: "#fff",
              boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              cursor: "pointer",
            }}
            title={titleCase(t("scroll to top"))}
          >
            ↑
          </button>
        )}
      </Container>
      <Modal
        isOpen={!!previewPhoto}
        toggle={() => setPreviewPhoto(null)}
        centered
        size="xl"
      >
        <ModalBody
          className="p-0 text-center bg-dark"
          style={{ overflow: "hidden" }}
        >
          {previewPhoto && (
            <img
              src={`${process.env.REACT_APP_FILES_URL}/${previewPhoto}`}
              alt=""
              style={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default LotView;